import { useState } from 'react'

import { useClipboard } from 'use-clipboard-copy'

import { Tag } from '@tribeplatform/gql-client/types'
import { useNetwork, useSpace } from '@tribeplatform/react-sdk/hooks'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { Input } from '@tribeplatform/react-ui-kit/Input'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { Slider } from '@tribeplatform/react-ui-kit/Slider'
import { Toggle } from '@tribeplatform/react-ui-kit/Toggle'

import { ErrorBoundary } from '../Error/ErrorBoundry'
import { SpaceTagSelector } from '../Space/SpaceTagSelector'

type IframeSrcParams = {
  readonly: true
  header: true
  composer: true
  about: true
  // eslint-disable-next-line camelcase
  tag_id?: string
}
const toQueryString = obj =>
  Object.keys(obj)
    .map(key => `${key}=${obj[key]}`)
    .join('&')

const getIframe = (iframeSrc, { height, title }) => {
  return `<iframe frameborder="no" src="${iframeSrc}" height="${height}px" width="100%" title="${title}" />`
}

export const SpaceEmbedCreator = ({ spaceId }) => {
  const [copied, setCopied] = useState(false)
  const clipboard = useClipboard()
  const { data: space, isFetching: fetchingSpace } = useSpace({
    variables: { id: spaceId },
  })
  const { data: network, isFetching: fetchingNetwork } = useNetwork()
  const loading = fetchingNetwork || fetchingSpace
  const [tag, setTag] = useState<Tag>()
  const baseUrl = `https://${network.domain}/embed/space/${space?.slug}`
  const [iframeHeight, setIframeHeight] = useState(360)
  const [iframeSrcParams, setIframeSrcParams] = useState<IframeSrcParams>({
    readonly: true,
    header: true,
    composer: true,
    about: true,
  })

  const iframeSrc = `${baseUrl}?${toQueryString(iframeSrcParams)}`
  const set = (key, value) => {
    setIframeSrcParams({
      ...iframeSrcParams,
      [key]: value,
    })
  }
  const unset = (key: keyof IframeSrcParams) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [key]: _deletedKey, ...rest } = iframeSrcParams
    setIframeSrcParams(rest as IframeSrcParams)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center w-full h-96">
        <SpinnerIcon className="animate-spin absolute h-9 w-9" />
      </div>
    )
  }

  const iframeToCopy = getIframe(iframeSrc, {
    height: iframeHeight,
    title: space?.name,
  })
  return (
    <>
      <Input
        onClick={e => e.currentTarget.select()}
        value={iframeToCopy}
        readOnly
        trailingAddon={
          <Link
            onClick={() => {
              clipboard.copy(iframeToCopy)
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            }}
            variant="neutral"
          >
            {copied ? 'Copied' : 'Copy'}
          </Link>
        }
      />
      <Divider />
      <div className="mb-3 space-y-5">
        <div className="flex justify-between items-center">
          <h5>Show Header</h5>
          <Toggle
            checked={iframeSrcParams.header}
            onChange={() => {
              set('header', !iframeSrcParams.header)
            }}
          />
        </div>
        <div className="flex justify-between items-center">
          <h5>Show Composer</h5>
          <Toggle
            checked={iframeSrcParams.composer}
            onChange={() => {
              set('composer', !iframeSrcParams.composer)
            }}
          />
        </div>
        <div className="flex justify-between items-center">
          <h5>Show About</h5>
          <Toggle
            checked={iframeSrcParams.about}
            onChange={() => {
              set('about', !iframeSrcParams.about)
            }}
          />
        </div>
        <div className="flex justify-between items-center">
          <h5>Read only</h5>
          <Toggle
            checked={iframeSrcParams.readonly}
            onChange={() => {
              set('readonly', !iframeSrcParams.readonly)
            }}
          />
        </div>
        <div className="flex justify-between items-center">
          <h5>Tag</h5>
          <div className="w-52">
            <SpaceTagSelector
              spaceId={space?.id}
              selectedTag={tag}
              onSelect={tag => {
                setTag(tag)
                if (!tag) {
                  unset('tag_id')
                } else {
                  set('tag_id', tag.id)
                }
              }}
            />
          </div>
        </div>
      </div>
      <h5>Embed height: {iframeHeight}px</h5>
      <div className="mb-2">
        <Slider
          value={iframeHeight}
          step={10}
          min={200}
          max={1000}
          onChange={setIframeHeight}
        />
      </div>
      <ErrorBoundary>
        <h5 className="mb-1">Embed preview</h5>
        <div className="p-3 bg-surface-300 overflow-auto h-96 rounded-md">
          <iframe
            src={iframeSrc}
            height={`${iframeHeight}px`}
            width="100%"
            title={space?.name}
          />
        </div>
      </ErrorBoundary>
    </>
  )
}
